import axios from 'axios';
import { clearAuth0Session } from '@/services/AuthService';

/**
 * 契約社員認証API
 */
class ContractorAuthApi {
  constructor() {
    this.baseUrl = process.env.VUE_APP_BASE_URL_API || 'http://localhost:8000';
    
    // 契約社員用のaxiosインスタンスを作成（認証ヘッダーを自動追加しない）
    this.api = axios.create({
      baseURL: this.baseUrl,
      timeout: 30000,
    });
  }

  /**
   * 契約社員のサインイン
   * @param {string} email メールアドレス
   * @param {string} password パスワード
   * @returns {Promise<Object>} レスポンス
   */
  async signIn(email, password) {
    try {
      console.log('ContractorAuthApi: Attempting to sign in');
      console.log('ContractorAuthApi: Base URL:', this.baseUrl);
      
      const response = await this.api.post('/contractor/signin', {
        email,
        password,
      });

      const data = response.data;
      
      if (data.success && data.token) {
        // 残留しているAuth0セッションをクリアしてから契約社員トークンを保存する
        // （併存するとAuth0側のトークンがサイレント復元され、どちらの身元で
        //   APIを呼ぶかが曖昧になるため）
        clearAuth0Session();

        // トークンをローカルストレージに保存
        localStorage.setItem('contractor_token', data.token);
        localStorage.setItem('contractor_email', email);
        return { success: true, message: data.message };
      } else {
        return { success: false, message: data.message };
      }
    } catch (error) {
      console.error('サインインエラー:', error);
      
      // axiosエラーレスポンスの処理
      if (error.response) {
        const errorMessage = error.response.data?.message || 'サインインに失敗しました';
        return { success: false, message: errorMessage };
      } else if (error.request) {
        return { 
          success: false, 
          message: 'サーバーに接続できませんでした。ネットワーク接続を確認してください。' 
        };
      } else {
        return { 
          success: false, 
          message: 'サインイン中にエラーが発生しました。' 
        };
      }
    }
  }

  /**
   * 契約社員のサインアウト
   * Auth0のトークンも削除して、別ユーザーとして再ログインできるようにする
   */
  signOut() {
    localStorage.removeItem('contractor_token');
    localStorage.removeItem('contractor_email');
    // Auth0のトークンも削除（別ユーザーとして再ログインする場合に備えて）
    localStorage.removeItem('DSR_ACCESS_TOKEN');
  }

  /**
   * 認証状態の確認
   * @returns {boolean} 認証済みかどうか
   */
  isAuthenticated() {
    return !!this.getToken();
  }

  /**
   * 認証済みユーザーのメールアドレスを取得
   * @returns {string|null} メールアドレス
   */
  getUserEmail() {
    return localStorage.getItem('contractor_email');
  }

  /**
   * 認証トークンを取得
   * @returns {string|null} JWT トークン
   */
  getToken() {
    // メモリにキャッシュせずlocalStorageを毎回参照する
    // （社員ログインへの切替等で外部からトークンが削除されても即座に反映するため）
    return localStorage.getItem('contractor_token');
  }

  /**
   * APIリクエスト用のヘッダーを取得
   * @returns {Object} ヘッダー
   */
  getAuthHeaders() {
    const token = this.getToken();
    if (!token) {
      throw new Error('認証が必要です');
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * JWT トークンの有効期限をチェック
   * @returns {boolean} トークンが有効かどうか
   */
  isTokenValid() {
    const token = this.getToken();
    if (!token) return false;

    try {
      // JWT のペイロード部分をデコード
      // ペイロードはbase64url（-, _, パディング無し）のためatob()用に変換する
      const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
      const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
      const payload = JSON.parse(atob(padded));
      const currentTime = Math.floor(Date.now() / 1000);

      // 有効期限をチェック
      return payload.exp > currentTime;
    } catch (error) {
      console.error('Token validation error:', error);
      return false;
    }
  }

  /**
   * トークンの自動更新チェック
   */
  checkAndRefreshToken() {
    if (!this.isTokenValid()) {
      this.signOut();
      return false;
    }
    return true;
  }
}

// シングルトンインスタンスを作成してエクスポート
const contractorAuthApi = new ContractorAuthApi();
export default contractorAuthApi;
